import {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

import { useToast } from "@chakra-ui/react";
import _keys from "lodash/keys";
import _values from "lodash/values";
import { useInterval } from "react-use";
import { useAccount, useProvider } from "wagmi";

// Components
import WalletConnectModal from "components/WalletConnectModal";
import AccountModal from "components/AccountModal";
import TransactionModal from "components/TransactionModal";
import AuditInfoModal from "components/AuditInfoModal";
import Referral from "components/Referral";

// Hooks
import useLocalStorage from "hooks/useLocalStorage";
import useReferral from "hooks/useReferral";
import useChain from "hooks/useChain";
import { useEmbed } from "hooks/useEmbed";

// Constants
import { TXN_STATUSES, TXN_STATUS, TXN_TYPES } from "constants/transactions";
import {
  ADD_BNBX,
  CLEAR_PAST_TRANSACTIONS,
  CLOSE_WALLET_MODAL,
  OPEN_ACCOUNT_MODAL,
  OPEN_AUDIT_MODAL,
  OPEN_WALLET_MODAL,
  PURCHASE_EVENT,
  TRANSACTION_FAILURE,
  TRANSACTION_SUCCESS,
  VIEW_ADDRESS_ON_SCANNER,
  VIEW_TXN_ON_SCANNER,
} from "constants/analytics";
import { WALLETS } from "constants/walletMenuOptions";
import { ReferralUserType } from "constants/referral";

// Utils
import { trackEvent } from "utils/firebase";

// Configs
import { config } from "config/network";
import {
  AnalyticsCallOptions,
  EventNameString,
  EventParams,
} from "firebase/analytics";
import useTokenBalance from "hooks/useTokenBalance";

type TxnData = {
  hash: string;
  status: TXN_STATUS;
  time: Date;
  amount: string | null;
  token: string;
  type: string;
};

type TxnDataMap = {
  [key: string]: TxnData;
};

const emptyFn = () => {
  undefined;
};

/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
const initialData = {
  handleViewOnEtherScan: emptyFn,
  initiateNewTxn: (show = true) => {},
  clearWalletData: emptyFn,
  updateTransactionDetails: (
    hash: string,
    type: string,
    amount: string | null,
    token: string
  ) => {},
  setTxnError: (error: string) => {},
  addTokenToUserWallet: emptyFn,
  openWalletModal: (params = {}) => {},
  openAccountModal: emptyFn,
  openAuditModal: emptyFn,
  closeModal: emptyFn,

  isTxnProcessing: false,
  isTxnModalOpen: false,
  openTxnHistoryModal: emptyFn,
  pendingTxnCount: 0,
  isEmbed: false,
  track: (
    eventName: EventNameString | string,
    eventParams: EventParams = {},
    options?: AnalyticsCallOptions
  ) => {},
};

const providerInitialData = {
  modalType: "",
  txnLoader: false,
  isTxnBatchOpen: false,
  showStrategy: true,
};

type PersistantDataType = {
  promptError: string;
  txnBatch: TxnDataMap;
  latestTxnHash: string;
};

const initialPersistantData = {
  promptError: "",
  txnBatch: {} as TxnDataMap,
  latestTxnHash: "",
};

export const AppContext = createContext(initialData);

const STADER_APPLICATION_CONTEXT = "stader-app-pers-data-store-binance";

const REFRESH_INTERVAL_TXN = 5000;

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const { address, isConnected, connector } = useAccount();

  const provider = useProvider();

  const { status } = useChain();

  const [providerData, setProviderData] = useState(providerInitialData);

  const [persistantData, setPersistantData] = useLocalStorage(
    STADER_APPLICATION_CONTEXT,
    initialPersistantData as PersistantDataType
  );

  const toast = useToast();

  const { token, xtoken } = useTokenBalance();

  const { userType, referralId, isValidReferralId } = useReferral();

  const commonAnalyticsEventParams = useMemo(
    () => ({
      wallet_name: persistantData.connectorPref || connector?.name || "",
      wallet_address: `address:${address || "N/A"}`,
      token_balance: token.balance,
      liquidtoken_balance: xtoken.balance,
      referralId: referralId || "N/A",
      userType: userType || "N/A",
      Blockchain: "Binance",
    }),
    [address, token, xtoken, persistantData, connector, referralId, userType]
  );

  const track = useCallback(
    (
      eventName: EventNameString | string,
      eventParams: EventParams = {},
      options?: AnalyticsCallOptions
    ) => {
      trackEvent(
        eventName,
        { ...eventParams, ...commonAnalyticsEventParams },
        options
      );
    },
    [commonAnalyticsEventParams]
  );

  const openWalletModal = useCallback((params = {}) => {
    trackEvent(OPEN_WALLET_MODAL, { ...params });
    setProviderData((prev) => ({ ...prev, modalType: "CONNECT_WALLET" }));
  }, []);

  const openAccountModal = useCallback(() => {
    trackEvent(OPEN_ACCOUNT_MODAL);
    setProviderData((prev) => ({ ...prev, modalType: "ACCOUNT" }));
  }, []);

  const openAuditModal = useCallback(() => {
    trackEvent(OPEN_AUDIT_MODAL);
    setProviderData((prev) => ({ ...prev, modalType: "AUDIT" }));
  }, []);

  const closeModal = useCallback(() => {
    trackEvent(CLOSE_WALLET_MODAL, { type: providerData.modalType });
    setProviderData((prev) => ({ ...prev, modalType: "" }));
  }, [providerData.modalType]);

  const resetLatestTransactionInfo = () => {
    setProviderData((prev) => ({
      ...prev,
      txnLoader: false,
    }));
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      promptError: "",
      latestTxnHash: "",
    }));
  };

  const initiateNewTxn = (show = true) => {
    setProviderData((prev) => ({
      ...prev,
      txnLoader: true,
      showStrategy: show,
    }));
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      promptError: "",
      latestTxnHash: "",
    }));
  };

  const clearWalletData = () => {
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: {},
      latestTxnHash: "",
      promptError: "",
    }));
  };

  const unsettledTransactions = _keys(persistantData.txnBatch).filter(
    (txn) => persistantData.txnBatch[txn].status === TXN_STATUSES.PENDING
  );

  const areTherePendingTxns = unsettledTransactions.length > 0;

  useInterval(async () => {
    if (areTherePendingTxns) {
      (async function fetchConfirmation() {
        const confirmations = await Promise.all(
          unsettledTransactions.map((txn: string) =>
            // TO-DO
            provider.getTransactionReceipt(txn)
          )
        );

        confirmations.forEach((confirmation) => {
          if (confirmation) {
            const { type, hash, amount } =
              persistantData.txnBatch[confirmation.transactionHash];
            if (confirmation.blockNumber && confirmation.status) {
              const a = address;
              const addr = a ? `address:${a}` : undefined;
              trackEvent(TRANSACTION_SUCCESS, {
                amount,
                hash: `txn:${hash}`,
                txnType: type,
                walletName: connector?.name,
                address: addr,
              });
              if (type === TXN_TYPES.STAKE) {
                trackEvent(PURCHASE_EVENT, {
                  value: +amount,
                  currency: "USD",
                });
              }
              updateTransactionDetailsWithResults(hash, TXN_STATUSES.SUCCESS);
              if (!providerData.txnLoader) {
                toast({
                  description: `${type} Transaction Successful`,
                  status: "success",
                  duration: 5000,
                  position: "top",
                  isClosable: true,
                });
              }
            } else {
              const a = address;
              const addr = a ? `address:${a}` : undefined;
              trackEvent(TRANSACTION_FAILURE, {
                amount,
                hash: `txn:${hash}`,
                txnType: type,
                walletName: connector?.name,
                address: addr,
              });
              updateTransactionDetailsWithResults(hash, TXN_STATUSES.ERROR);
              if (!providerData.txnLoader) {
                toast({
                  description: `${type} Transaction failed`,
                  status: "error",
                  duration: 5000,
                  position: "top",
                  isClosable: true,
                });
              }
            }
          }
        });
      })();
    }
  }, REFRESH_INTERVAL_TXN);

  const updateTransactionDetails = (
    hash: string,
    type: string,
    amount: string | null,
    token: string
  ) => {
    const newTxn = {
      hash,
      status: TXN_STATUSES.PENDING,
      time: new Date(),
      amount,
      token,
      type,
    };
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: { ...prev.txnBatch, [hash]: newTxn },
      latestTxnHash: hash,
    }));
  };

  const updateTransactionDetailsWithResults = (
    hash: string,
    status: string
  ) => {
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      txnBatch: {
        ...prev.txnBatch,
        [hash]: { ...prev.txnBatch[hash], status },
      },
    }));
  };

  const setTxnError = (error: string) => {
    setPersistantData((prev: PersistantDataType) => ({
      ...prev,
      promptError: error,
    }));
  };

  const handleViewOnEtherScan = useCallback(() => {
    const a = address;
    const addr = a ? `address:${a}` : undefined;
    trackEvent(VIEW_ADDRESS_ON_SCANNER, { address: addr });
    const url = `${config.blockExplorerUrls}/address/${address}`;

    window.open(url, "_blank");
  }, [address]);

  const handleTxViewOnEtherScan = (hash = persistantData.latestTxnHash) => {
    const a = address;
    const addr = a ? `address:${a}` : undefined;
    trackEvent(VIEW_TXN_ON_SCANNER, { address: addr, hash });
    const url = `${config.blockExplorerUrls}/tx/${hash}`;

    window.open(url, "_blank");
  };

  const addXTokenToWallet = async () => {
    if (connector) {
      if (
        [WALLETS.BINANCE, WALLETS.MATH_WALLET, WALLETS.NABOX].includes(
          connector.id as WALLETS
        )
      ) {
        toast({
          description: `Feature not supported on the wallet type yet.`,
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      } else if (connector.watchAsset) {
        await connector?.watchAsset({
          address: config.contractAddresses.xtoken.token,
          symbol: config.nativeCurrency.xsymbol,
        });
      }
    }
  };

  const addTokenToUserWallet = async () => {
    trackEvent(ADD_BNBX);
    addXTokenToWallet();
  };

  const isEmbed = useEmbed();

  const contextData = {
    handleViewOnEtherScan,
    initiateNewTxn,
    clearWalletData,
    updateTransactionDetails,
    setTxnError,
    addTokenToUserWallet,
    openWalletModal,
    openAccountModal,
    openAuditModal,
    closeModal,
    isTxnProcessing: areTherePendingTxns,
    isTxnModalOpen: providerData.txnLoader,
    openTxnHistoryModal: () =>
      setProviderData((prev) => ({ ...prev, isTxnBatchOpen: true })),
    pendingTxnCount: unsettledTransactions.length,
    isEmbed,
    track,
  };

  return (
    <AppContext.Provider value={contextData}>
      {children}
      <WalletConnectModal
        isOpen={providerData.modalType === "CONNECT_WALLET" && !isConnected}
      />
      <AccountModal
        isOpen={providerData.modalType === "ACCOUNT"}
        handleTxView={handleTxViewOnEtherScan}
        txnBatchData={_values(persistantData.txnBatch).sort(
          (txn1, txn2) =>
            new Date(txn2.time).getTime() - new Date(txn1.time).getTime()
        )}
        onClear={() => {
          track(CLEAR_PAST_TRANSACTIONS);
          setPersistantData((prev: PersistantDataType) => ({
            ...prev,
            txnBatch: {},
          }));
        }}
      />
      <TransactionModal
        isOpen={providerData.txnLoader}
        hash={persistantData.latestTxnHash}
        error={
          persistantData.promptError ||
          (persistantData.txnBatch[persistantData.latestTxnHash] &&
            persistantData.txnBatch[persistantData.latestTxnHash].status ===
              TXN_STATUSES.ERROR)
        }
        isTxnProcessing={
          persistantData.txnBatch[persistantData.latestTxnHash]
            ? persistantData.txnBatch[persistantData.latestTxnHash].status ===
              TXN_STATUSES.PENDING
            : true
        }
        closeAlert={resetLatestTransactionInfo}
        handleTxView={handleTxViewOnEtherScan}
        showStrategy={providerData.showStrategy}
      />
      <AuditInfoModal isOpen={providerData.modalType === "AUDIT"} />
      {status &&
        referralId &&
        userType &&
        userType !== ReferralUserType.KOL && (
          <Referral
            user={userType}
            referralId={referralId as string}
            isValidReferralId={isValidReferralId}
          />
        )}
    </AppContext.Provider>
  );
};
